<template>
  <section class="create-product">
    <div v-if="showErrorModal" class="error-modal-overlay">
      <div class="error-modal-content">
        <div class="error-modal-header">
          <i class="fa-solid fa-circle-exclamation error-modal-icon" />
          <h3>Creation failed</h3>
          <button type="button" class="error-modal-close" @click="closeErrorModal">
            <i class="fa-solid fa-xmark" />
          </button>
        </div>
        <div class="error-modal-body">
          <p>{{ errorMessage }}</p>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div v-if="created" class="ack-container">
        <h2>
          <i class="fa-solid fa-circle-check" style="color: #4caf50" />
          Kids product created
        </h2>
        <p>Item #{{ createdItemId }} was added to the kids store.</p>
        <div class="success-actions">
          <button type="button" class="continue-btn" @click="resetForm">
            <i class="fa-solid fa-plus-circle" /> Create another
          </button>
        </div>
      </div>

      <div v-else class="form-wrapper">
        <div class="form-header">
          <h1>Create kids product</h1>
          <p class="form-subtitle">
            Add equipment to the kids store by development stage and subcategory
          </p>
          <p v-if="!canCreate" class="permission-warning">
            <i class="fa-solid fa-lock" /> Only support and super admin can create listings.
          </p>
        </div>

        <form class="product-form" @submit.prevent="submit">
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-child-reaching" />
              <h3>Classification</h3>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label for="development_stage">
                  Development stage <span class="required">*</span>
                </label>
                <select
                  id="development_stage"
                  v-model.number="form.development_stage"
                  required
                  :disabled="isLoading || !canCreate"
                  @change="onStageChange"
                >
                  <option :value="null">Select stage</option>
                  <option v-for="s in stages" :key="s.stage" :value="s.stage">
                    {{ s.label }}
                  </option>
                </select>
              </div>

              <div v-if="isAdvanced" class="form-group">
                <label for="sport_code">Sport <span class="required">*</span></label>
                <select
                  id="sport_code"
                  v-model.number="form.sport_code"
                  required
                  :disabled="isLoading || !canCreate"
                  @change="onSportChange"
                >
                  <option :value="null">Select sport</option>
                  <option v-for="sp in sports" :key="sp.sport" :value="sp.sport">
                    {{ sp.label }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label for="sub_category">Subcategory <span class="required">*</span></label>
                <select
                  id="sub_category"
                  v-model="form.sub_category"
                  required
                  :disabled="isLoading || !canCreate || !form.development_stage || (isAdvanced && !form.sport_code)"
                >
                  <option value="">Select subcategory</option>
                  <option v-for="sub in filteredSubcategories" :key="sub.slug" :value="sub.slug">
                    {{ sub.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-info-circle" />
              <h3>Details</h3>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label for="name">Name <span class="required">*</span></label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  required
                  maxlength="120"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group">
                <label for="slug">Slug</label>
                <input
                  id="slug"
                  v-model="form.slug"
                  type="text"
                  placeholder="auto-generated if empty"
                  maxlength="150"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group">
                <label for="brand">Brand</label>
                <input
                  id="brand"
                  v-model="form.brand"
                  type="text"
                  maxlength="100"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div v-if="showSizeField" class="form-group">
                <label for="size">Size</label>
                <input
                  id="size"
                  v-model="form.size"
                  type="text"
                  placeholder="e.g. EU 28 or XS"
                  maxlength="50"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group full-width">
                <label for="short_description">Short description</label>
                <input
                  id="short_description"
                  v-model="form.short_description"
                  type="text"
                  maxlength="255"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group full-width">
                <label for="description">Description</label>
                <textarea
                  id="description"
                  v-model="form.description"
                  rows="4"
                  :disabled="isLoading || !canCreate"
                />
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-tag" />
              <h3>Pricing &amp; stock</h3>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label for="price">Price (KES) <span class="required">*</span></label>
                <input
                  id="price"
                  v-model.number="form.price"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group">
                <label for="cost_price">Cost price</label>
                <input
                  id="cost_price"
                  v-model.number="form.cost_price"
                  type="number"
                  min="0"
                  step="0.01"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group">
                <label for="stock_quantity">Stock</label>
                <input
                  id="stock_quantity"
                  v-model.number="form.stock_quantity"
                  type="number"
                  min="0"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group">
                <label for="low_stock_threshold">Low stock alert</label>
                <input
                  id="low_stock_threshold"
                  v-model.number="form.low_stock_threshold"
                  type="number"
                  min="0"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group checkbox-group">
                <label>
                  <input
                    v-model="form.discounted"
                    type="checkbox"
                    :disabled="isLoading || !canCreate"
                  />
                  On discount
                </label>
              </div>
              <div v-if="form.discounted" class="form-group">
                <label for="discount_price">Discount price</label>
                <input
                  id="discount_price"
                  v-model.number="form.discount_price"
                  type="number"
                  min="0"
                  step="0.01"
                  :disabled="isLoading || !canCreate"
                />
              </div>
              <div class="form-group checkbox-group">
                <label>
                  <input
                    v-model="form.is_active"
                    type="checkbox"
                    :disabled="isLoading || !canCreate"
                  />
                  Active listing
                </label>
              </div>
              <div class="form-group checkbox-group">
                <label>
                  <input
                    v-model="form.returnable"
                    type="checkbox"
                    :disabled="isLoading || !canCreate"
                  />
                  Returnable
                </label>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-image" />
              <h3>Images</h3>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label>Thumbnail <span class="required">*</span></label>
                <input
                  ref="thumbnailInput"
                  type="file"
                  accept="image/*"
                  hidden
                  @change="onThumbnailChange"
                />
                <button
                  type="button"
                  class="file-btn"
                  :disabled="isLoading || !canCreate"
                  @click="$refs.thumbnailInput?.click()"
                >
                  {{ selectedThumbnail ? selectedThumbnail.name : "Choose thumbnail" }}
                </button>
              </div>
              <div class="form-group">
                <label>Gallery</label>
                <input
                  ref="imagesInput"
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  @change="onImagesChange"
                />
                <button
                  type="button"
                  class="file-btn"
                  :disabled="isLoading || !canCreate"
                  @click="$refs.imagesInput?.click()"
                >
                  {{ selectedImages.length ? `${selectedImages.length} file(s)` : "Add images" }}
                </button>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="submit-btn" :disabled="isLoading || !canCreate">
              <i v-if="isLoading" class="fa-solid fa-spinner fa-spin" />
              <template v-else>Create kids product</template>
            </button>
            <button type="button" class="cancel-btn" :disabled="isLoading" @click="resetForm">
              Reset
            </button>
          </div>
        </form>
      </div>
    </transition>
  </section>
</template>

<script>
import generateHmacSignature from "@/utils/utils";
import { buildApiUrl } from "@/config/api";
import { useAdminAuthStore } from "@/authen/adminAuth";
import {
  KIDS_SPORTS,
  KIDS_STAGES,
  isAdvancedStage,
  subcategoriesForStage,
  subcategoryNeedsSize,
} from "@/constants/kidsCatalogue";

const ALLOWED_ROLES = ["support", "super_admin"];

const emptyForm = () => ({
  development_stage: null,
  sport_code: null,
  sub_category: "",
  name: "",
  slug: "",
  brand: "",
  short_description: "",
  description: "",
  price: null,
  discount_price: null,
  discounted: false,
  cost_price: null,
  currency: "KES",
  stock_quantity: 0,
  low_stock_threshold: 10,
  is_active: true,
  returnable: true,
  size: "",
  tags: [],
  images: [],
  thumbnail: "",
});

export default {
  name: "CreateKidsProduct",
  data() {
    return {
      created: false,
      createdItemId: null,
      isLoading: false,
      showErrorModal: false,
      errorMessage: "",
      stages: KIDS_STAGES,
      sports: KIDS_SPORTS,
      selectedThumbnail: null,
      selectedImages: [],
      form: emptyForm(),
    };
  },
  computed: {
    canCreate() {
      const store = useAdminAuthStore();
      const role = (store.admin?.role || "").toLowerCase();
      return ALLOWED_ROLES.includes(role);
    },
    isAdvanced() {
      return isAdvancedStage(this.form.development_stage);
    },
    filteredSubcategories() {
      return subcategoriesForStage(this.form.development_stage, this.form.sport_code);
    },
    showSizeField() {
      return subcategoryNeedsSize(this.form.sub_category);
    },
  },
  methods: {
    onStageChange() {
      this.form.sport_code = null;
      this.form.sub_category = "";
      this.form.size = "";
    },
    onSportChange() {
      this.form.sub_category = "";
      this.form.size = "";
    },
    onThumbnailChange(event) {
      const file = event.target.files?.[0];
      this.selectedThumbnail = file || null;
    },
    onImagesChange(event) {
      this.selectedImages = Array.from(event.target.files || []).filter((f) =>
        f.type.startsWith("image/"),
      );
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = "";
    },
    resetForm() {
      this.created = false;
      this.createdItemId = null;
      this.showErrorModal = false;
      this.errorMessage = "";
      this.selectedThumbnail = null;
      this.selectedImages = [];
      this.form = emptyForm();
      if (this.$refs.thumbnailInput) this.$refs.thumbnailInput.value = "";
      if (this.$refs.imagesInput) this.$refs.imagesInput.value = "";
    },
    async submit() {
      if (!this.canCreate) {
        this.errorMessage = "You do not have permission to create kids products.";
        this.showErrorModal = true;
        return;
      }

      if (
        !this.form.development_stage ||
        !this.form.sub_category ||
        !this.form.name?.trim() ||
        !this.form.price ||
        !this.selectedThumbnail
      ) {
        this.errorMessage = "Stage, subcategory, name, price, and thumbnail are required.";
        this.showErrorModal = true;
        return;
      }

      if (this.isAdvanced && !this.form.sport_code) {
        this.errorMessage = "Sport is required for Advanced Play products.";
        this.showErrorModal = true;
        return;
      }

      if (this.form.discounted && (this.form.discount_price == null || this.form.discount_price === "")) {
        this.errorMessage = "Discount price is required when the product is on discount.";
        this.showErrorModal = true;
        return;
      }

      const adminStore = useAdminAuthStore();
      adminStore.checkToken();
      if (!adminStore.isAuthenticated) {
        this.errorMessage = "Session expired. Please sign in again.";
        this.showErrorModal = true;
        return;
      }

      this.isLoading = true;
      try {
        const payloadForHmac = {
          development_stage: Number(this.form.development_stage),
          sport_code: this.isAdvanced ? Number(this.form.sport_code) : null,
          sub_category: this.form.sub_category,
          name: this.form.name.trim(),
          slug: (this.form.slug || "").trim(),
          brand: (this.form.brand || "").trim(),
          short_description: this.form.short_description || "",
          description: this.form.description || "",
          price: Number(this.form.price),
          discount_price: this.form.discounted ? Number(this.form.discount_price) : null,
          discounted: Boolean(this.form.discounted),
          cost_price: this.form.cost_price ?? null,
          currency: this.form.currency || "KES",
          stock_quantity: Number(this.form.stock_quantity) || 0,
          low_stock_threshold: Number(this.form.low_stock_threshold) || 10,
          is_active: Boolean(this.form.is_active),
          returnable: Boolean(this.form.returnable),
          size: this.showSizeField ? (this.form.size || "").trim() || null : null,
          tags: this.form.tags || [],
          images: [],
          thumbnail: "",
        };

        const formData = new FormData();
        formData.append("payload", JSON.stringify(generateHmacSignature(payloadForHmac)));
        formData.append("thumbnail", this.selectedThumbnail);
        this.selectedImages.forEach((file) => formData.append("images", file));

        const token = (adminStore.accessToken || localStorage.getItem("adminAccessToken") || "").trim();
        const res = await fetch(buildApiUrl("/post/kids/create"), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        let data = {};
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = {};
          }
        }

        if (!res.ok) {
          const detail = Array.isArray(data.detail)
            ? data.detail.map((e) => e.msg || e).join("; ")
            : data.detail || data.message || "Could not create kids product.";
          throw new Error(detail);
        }

        this.createdItemId = data.item_id;
        this.created = true;
      } catch (err) {
        this.errorMessage = err.message || "Unexpected error.";
        this.showErrorModal = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.create-product {
  padding: 1.5rem;
  max-width: 960px;
  margin: 0 auto;
}
.form-wrapper {
  background: var(--background-color2);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid var(--background-color3);
}
.form-header h1 {
  margin: 0 0 0.35rem;
  font-size: 1.5rem;
}
.form-subtitle {
  margin: 0 0 1rem;
  color: var(--text-color2);
}
.permission-warning {
  color: #c62828;
  font-size: 0.9rem;
}
.form-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--background-color3);
}
.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.section-header h3 {
  margin: 0;
  font-size: 1rem;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}
.form-group label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
}
.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.55rem 0.65rem;
  border-radius: 8px;
  border: 1px solid var(--background-color3);
  background: var(--background-color1);
  color: var(--text-color1);
}
.required {
  color: #c62828;
}
.form-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.submit-btn,
.cancel-btn,
.continue-btn,
.file-btn {
  font: inherit;
}
.submit-btn,
.continue-btn {
  padding: 0.65rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: var(--border-color-hover, #2563eb);
  color: #fff;
  cursor: pointer;
}
.cancel-btn {
  padding: 0.65rem 1.25rem;
  border-radius: 8px;
  border: 1px solid var(--background-color3);
  background: transparent;
  cursor: pointer;
}
.submit-btn:disabled,
.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.ack-container {
  text-align: center;
  padding: 3rem 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.06);
}
.success-actions {
  margin-top: 1.25rem;
}
.error-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.error-modal-content {
  background: var(--background-color2);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  max-width: 420px;
  width: 90%;
}
.error-modal-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.error-modal-close {
  margin-left: auto;
  border: none;
  background: none;
  cursor: pointer;
}
.full-width {
  grid-column: 1 / -1;
}
.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}
.file-btn {
  width: 100%;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  border: 1px dashed var(--background-color3);
  background: var(--background-color1);
  cursor: pointer;
  text-align: left;
}
.file-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
